import React, { DragEvent, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import Icon from 'carbon-react/lib/components/icon';
import Textbox from 'carbon-react/lib/components/textbox';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { ArrowScroll } from '../arrow-scroll/arrow-scroll';
import { ReportCard } from '../report-card/report-card';
import { ReportTemplate } from '../../types';
import { TagsFilter } from '../tags-filter/tags-filter';
import { useI18nConstants } from '../../use-i18n-constants';
import { useReportBuilderContext } from '../../report-builder.context';
import { useReportsContext } from '../../reports.context';
import { useTemplatesContext } from '../../templates.context';

import styles from './templates-panel.scss';

const ORIENTATIONS: ('portrait' | 'landscape' | 'dashboard')[] = ['portrait', 'landscape', 'dashboard'];

export const TemplatesPanel = ({
  onTemplateDragStart
}: {
  onTemplateDragStart: (e: DragEvent<HTMLDivElement | HTMLSpanElement>, templateId: string) => void;
}) => {
  const intl = useIntl();
  const { templates, workspaceTemplates } = useTemplatesContext();
  const { isGuest } = useReportsContext();
  const { orientations: orientationDisplayableNames } = useI18nConstants();
  const { pageSetup, isWorkspaceTemplate, isTemplate } = useReportBuilderContext();
  const [searchTxt, setSearchTxt] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const publishedTemplates = useMemo(() => templates.filter(t => t.status === 'published'), [templates]);
  const workspacePublishedTemplates = useMemo(() => workspaceTemplates.filter(t => t.status === 'published'), [
    workspaceTemplates
  ]);
  const [sameOrientTemplates, setSameOrientTemplates] = useState(publishedTemplates);
  const [otherOrientTemplates1, setOtherOrientTemplates1] = useState(publishedTemplates);
  const [otherOrientTemplates2, setOtherOrientTemplates2] = useState(publishedTemplates);
  const [sameOrientWTemplates, setSameOrientWTemplates] = useState(workspacePublishedTemplates);
  const [otherOrientWTemplates1, setOtherOrientWTemplates1] = useState(workspacePublishedTemplates);
  const [otherOrientWTemplates2, setOtherOrientWTemplates2] = useState(workspacePublishedTemplates);
  const [orientations, setOrientations] = useState<('portrait' | 'landscape' | 'dashboard')[]>([...ORIENTATIONS]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTxt(e.target.value);
  };

  useEffect(() => {
    const remainingOrientations = [...ORIENTATIONS].filter(o => o !== pageSetup.orientation);
    const newFiltredTemplates = publishedTemplates
      .filter(
        t => t.name.toLowerCase().includes(searchTxt.toLowerCase()) && (!selectedTag || t.tags.includes(selectedTag))
      )
      .sort((a, b) => a.name.localeCompare(b.name));
    setSameOrientTemplates(newFiltredTemplates.filter(t => t.orientation === pageSetup.orientation));
    setOtherOrientTemplates1(newFiltredTemplates.filter(t => t.orientation === remainingOrientations[0]));
    setOtherOrientTemplates2(newFiltredTemplates.filter(t => t.orientation === remainingOrientations[1]));
    setOrientations(remainingOrientations);
  }, [publishedTemplates, searchTxt, selectedTag, pageSetup.orientation]);

  useEffect(() => {
    const remainingOrientations = [...ORIENTATIONS].filter(o => o !== pageSetup.orientation);
    const newFiltredTemplates = workspacePublishedTemplates
      .filter(t => t.name.toLowerCase().includes(searchTxt.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
    setSameOrientWTemplates(newFiltredTemplates.filter(t => t.orientation === pageSetup.orientation));
    setOtherOrientWTemplates1(newFiltredTemplates.filter(t => t.orientation === remainingOrientations[0]));
    setOtherOrientWTemplates2(newFiltredTemplates.filter(t => t.orientation === remainingOrientations[1]));
    setOrientations(remainingOrientations);
  }, [workspacePublishedTemplates, searchTxt, pageSetup.orientation]);

  const handleTagChange = (tag: string) => setSelectedTag(selectedTag === tag ? '' : tag);

  const getOrientationName = (name: 'portrait' | 'landscape' | 'dashboard') => orientationDisplayableNames[name];

  const renderTemplatesArray = (
    templatesArr: ReportTemplate[],
    templatesOrientation: 'portrait' | 'landscape' | 'dashboard'
  ) =>
    templatesArr.length > 0 ? (
      <div className={clsx(styles.templatesSection)}>
        <h4>
          {getOrientationName(templatesOrientation)}
          {templatesOrientation !== pageSetup.orientation && (
            <Icon
              type="error"
              fontSize="small"
              tooltipMessage={
                <FormattedMessage
                  id="reporting.should_change_orientation"
                  defaultMessage="You need to change the report's orientation in order to use these templates"
                />
              }
              tooltipPosition="bottom"
              tooltipBgColor="#5d6579"
              ml="4px"
            />
          )}
        </h4>
        <ArrowScroll>
          <div className={clsx(styles.cardsContainer)}>
            {templatesArr.map(t => {
              return (
                <ReportCard
                  key={t.id}
                  isTemplate
                  isDisabled={templatesOrientation !== pageSetup.orientation}
                  orientation={templatesOrientation}
                  report={t}
                  isDraggable={templatesOrientation === pageSetup.orientation}
                  onDragStart={e =>
                    templatesOrientation === pageSetup.orientation ? onTemplateDragStart(e, t.id) : null
                  }
                />
              );
            })}
          </div>
        </ArrowScroll>
      </div>
    ) : null;

  return (
    <div className={clsx(styles.templatesContainer)}>
      <Textbox
        placeholder={intl.formatMessage({ id: 'reporting.search_templates', defaultMessage: 'Search templates...' })}
        value={searchTxt}
        inputIcon="search"
        onChange={handleSearch}
        mb={2}
      />
      {!isGuest && (!isTemplate || isWorkspaceTemplate) && workspacePublishedTemplates.length > 0 && (
        <>
          <h3>
            <FormattedMessage id="reporting.workspace_templates" defaultMessage="Workspace templates" />
          </h3>
          <div>
            {renderTemplatesArray(sameOrientWTemplates, pageSetup.orientation)}
            {renderTemplatesArray(otherOrientWTemplates1, orientations[0])}
            {renderTemplatesArray(otherOrientWTemplates2, orientations[1])}
          </div>
        </>
      )}
      <h3>
        <FormattedMessage id="reporting.futrli_templates" defaultMessage="Futrli templates" />
      </h3>
      <TagsFilter selectedTag={selectedTag} onChange={handleTagChange} />
      <div>
        {renderTemplatesArray(sameOrientTemplates, pageSetup.orientation)}
        {renderTemplatesArray(otherOrientTemplates1, orientations[0])}
        {renderTemplatesArray(otherOrientTemplates2, orientations[1])}
      </div>
    </div>
  );
};
